import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cleanInput, getErrorMessage, ErrorMessages } from '@/lib/utils';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useServiceWithAuth } from '@/hooks/useServiceWithAuth';
import { CommunicationService } from '@/services/features/CommunicationService';

interface EmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: number | null;
  onSuccess: () => void;
}

const EmailDialog = ({ open, onOpenChange, groupId, onSuccess }: EmailDialogProps) => {
  const { token, user } = useAuth();
  const { handleError, handleSuccess } = useErrorHandler();
  const [emails, setEmails] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Get properly initialized service with auth context
  const communicationService = useServiceWithAuth(CommunicationService);

  const resetForm = () => {
    setEmails('');
    setPassword('');
  };

  // Clear form fields whenever dialog opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateEmails = (emailString: string) => {
    if (!emailString.trim()) return { valid: false, message: ErrorMessages.REQUIRED_FIELD };
    
    const emailList = emailString.split(',').map(email => email.trim()).filter(email => email);
    
    if (emailList.length === 0) {
      return { valid: false, message: "Please enter at least one email address" };
    }

    const invalidEmails = emailList.filter(email => !validateEmail(email));
    
    if (invalidEmails.length > 0) {
      return { 
        valid: false, 
        message: `Invalid email format: ${invalidEmails.join(', ')}` 
      };
    }

    return { valid: true, message: "", emails: emailList };
  };

  const handleSend = async () => {
    if (!password.trim()) {
      handleError(new Error("Password is required"), "Validation Error");
      return;
    }

    const emailValidation = validateEmails(cleanInput(emails));
    if (!emailValidation.valid) {
      handleError(new Error(emailValidation.message), "Email Validation Error");
      return;
    }

    if (!user?.id || !groupId) {
      handleError(new Error("Missing user ID or group ID"));
      return;
    }

    setIsLoading(true);
    try {
      const result = await communicationService.sendGroupDetailsEmail(
        groupId,
        emailValidation.emails!,
        cleanInput(password)
      );
      
      handleSuccess(`Email sent successfully to ${emailValidation.emails?.length} recipient(s)!`);
      onOpenChange(false);
      onSuccess();
      resetForm();
    } catch (error) {
      handleError(error, "Failed to send email");
    } finally {
      setIsLoading(false);
    }
  };


  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Email Patient Sheet</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enter Email Address
            </label>
            <Input
              placeholder="Enter email addresses separated by commas..."
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              className="w-full"
              autoComplete="off"
            />
            <p className="text-xs text-purple-600 mt-1">
              💡 Enter comma to add multiple email addresses.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enter Password to Protect Excel Sheet
            </label>
            <Input
              type="password"
              placeholder="Password is required"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full"
              autoComplete="new-password"
            />
            {!password && (
              <p className="text-xs text-red-600 mt-1">
                Enter Password to Protect Excel Sheet is required
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex space-x-2">
          <Button
            onClick={handleClose}
            className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white rounded-full px-8"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isLoading}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-full px-8"
          >
            {isLoading ? 'Sending...' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailDialog;